"use client";

/**
 * DashMock — animated mock of the internal admin Dash surface for
 * /apps/dash. Deliberately reinforces "this is an internal tool" — a
 * locked banner across the top, generic admin-only sidebar.
 *
 * IMPORTANT: every name in this mock is fake. No service names, no
 * database/cache/secrets-store names, no internal security identifiers
 * (no V-numbers, no role names from the real Prisma schema). Copy stays
 * generic — "admin authentication required" rather than naming the
 * specific gate. Numbers are illustrative only.
 *
 * NOT the real dashboard. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Lock,
  Receipt,
  TrendingUp,
  Activity,
  Server,
  Database,
  ShieldCheck,
  Users,
  Layers,
  DollarSign,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  ArrowUpRight,
} from "lucide-react";
import { NumberTicker } from "@/components/marketing/number-ticker";

type NavItem = {
  key: string;
  label: string;
  icon: typeof Receipt;
  active?: boolean;
  badge?: string;
};

const SIDEBAR: NavItem[] = [
  { key: "revenue", label: "Revenue", icon: TrendingUp, active: true },
  { key: "pricing", label: "Pricing", icon: Receipt },
  { key: "models", label: "Models", icon: Layers, badge: "12" },
  { key: "analytics", label: "Analytics", icon: Activity },
  { key: "services", label: "Services", icon: Server },
  { key: "audit", label: "Audit", icon: ShieldCheck },
  { key: "users", label: "Users", icon: Users },
  { key: "system", label: "System", icon: Database },
];

type Kpi = {
  key: string;
  label: string;
  icon: typeof DollarSign;
  value: number;
  prefix?: string;
  suffix?: string;
  delta: string;
  trend: "up" | "down" | "flat";
};

const KPIS: Kpi[] = [
  {
    key: "mrr",
    label: "Monthly recurring revenue",
    icon: DollarSign,
    value: 184320,
    prefix: "$",
    delta: "+12.4%",
    trend: "up",
  },
  {
    key: "seats",
    label: "Active seats",
    icon: Users,
    value: 1842,
    delta: "+58 wk",
    trend: "up",
  },
  {
    key: "latency",
    label: "P95 latency",
    icon: Activity,
    value: 312,
    suffix: " ms",
    delta: "−18 ms",
    trend: "down", // down latency is good — accent stays neutral here
  },
  {
    key: "storage",
    label: "Storage cost / mo",
    icon: Database,
    value: 38,
    prefix: "$",
    delta: "+$4",
    trend: "up",
  },
];

// Generic operator-console service names. Deliberately generic — no
// internal app names, no DB/cache/secrets-store vendor names, no
// monorepo paths. Numbers are illustrative.
type Service = { name: string; status: "ok" | "warn" | "down"; p95: number; rps: number };
const SERVICES: Service[] = [
  { name: "account-service · primary",  status: "ok",   p95: 42,  rps: 1840 },
  { name: "model-router · live",        status: "warn", p95: 612, rps: 27 },
  { name: "ingest-pipeline · primary",  status: "ok",   p95: 31,  rps: 904 },
  { name: "billing-engine · live",      status: "ok",   p95: 12,  rps: 1200 },
  { name: "analytics-collector",        status: "ok",   p95: 64,  rps: 88 },
  { name: "auth-broker · cluster",      status: "ok",   p95: 8,   rps: 41 },
  { name: "comms-relay · sms+email",    status: "ok",   p95: 18,  rps: 76 },
  { name: "notification-bus · async",   status: "ok",   p95: 4,   rps: 2310 },
];

export function DashMock() {
  const [refreshing, setRefreshing] = useState(false);
  // Partial-fill: services table is already rendered on first paint so the
  // operator console reads "live" not "loading". NumberTicker on the KPI grid
  // is left alone — it still ticks up nicely.
  const [tableArmed, setTableArmed] = useState(true);
  const [secondsAgo, setSecondsAgo] = useState(11);
  const [activeNav, setActiveNav] = useState("revenue");
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runCycle = () => {
      // Don't reset the table to opacity-0 — keep the partial-fill snapshot
      // visible across the entire loop.
      setTableArmed(true);
      schedule(() => setRefreshing(true), 500);
      schedule(() => setRefreshing(false), 1800);
      schedule(() => setSecondsAgo(0), 1800);
      // Loop ~ every 6s
      schedule(runCycle, 6000);
    };

    // Time-since counter tick.
    const tick = setInterval(() => setSecondsAgo((n) => n + 1), 1000);

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
      clearInterval(tick);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* ADMIN BANNER — accent red dot + locked label */}
        <div className="flex items-center gap-2.5 px-4 py-2 bg-[hsl(var(--accent))]/10 border-b hairline-strong text-[10px] font-mono tracking-[0.18em] uppercase">
          <Lock className="h-3 w-3 text-[hsl(var(--accent))]" />
          <span className="text-foreground/85 font-semibold">Admin only · internal tool</span>
          <span className="hidden sm:inline text-foreground/65">· staff and approved operators</span>
          <span className="ml-auto hidden sm:inline-flex items-center gap-1.5 text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            not a public product
          </span>
        </div>

        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            dash.askfinz.ai · live · internal
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (refreshing ? "bg-[hsl(var(--accent))] animate-pulse" : "bg-foreground/30")
              }
            />
            {refreshing ? "Refreshing" : `Synced · ${secondsAgo}s ago`}
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[520px]">
          {/* Sidebar */}
          <aside className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r hairline bg-foreground/[0.02] p-3 flex md:flex-col gap-1 overflow-x-auto">
            <div className="hidden md:block px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.18em] uppercase text-foreground/65">
              Operator console
            </div>
            {SIDEBAR.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === activeNav;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveNav(item.key)}
                  className={
                    "shrink-0 inline-flex md:flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-left transition " +
                    (isActive
                      ? "bg-foreground text-background"
                      : "text-foreground/65 hover:bg-foreground/5")
                  }
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="md:flex-1">{item.label}</span>
                  {item.badge && (
                    <span
                      className={
                        "hidden md:inline-flex h-4 min-w-[20px] px-1 items-center justify-center rounded-[4px] text-[10px] font-mono " +
                        (isActive
                          ? "bg-background/20 text-background"
                          : "border hairline-strong text-foreground/65")
                      }
                    >
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <ChevronRight className="hidden md:inline h-3 w-3" />
                  )}
                </button>
              );
            })}
          </aside>

          {/* Main pane */}
          <main className="col-span-12 md:col-span-9 flex flex-col">
            {/* Header strip */}
            <div className="flex items-center gap-3 px-4 py-3 border-b hairline">
              <div>
                <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-foreground/65">
                  Revenue
                </div>
                <div className="text-[15px] font-medium tracking-tight">
                  Plan & seat ledger — 2026 May
                </div>
              </div>
              <button
                type="button"
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border hairline-strong px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-foreground/65 hover:bg-foreground/5 transition"
              >
                <RefreshCw className={"h-3 w-3 " + (refreshing ? "animate-spin" : "")} />
                {refreshing ? "Refreshing" : "Refresh"}
              </button>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 p-4">
              {KPIS.map((k) => (
                <KpiTile key={k.key} kpi={k} />
              ))}
            </div>

            {/* Services table */}
            <div className="flex-1 px-4 pb-4">
              <div className="rounded-xl border hairline bg-background overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 border-b hairline text-[10px] font-mono tracking-[0.16em] uppercase text-foreground/65">
                  <Server className="h-3 w-3" />
                  Services · live p95
                  <span className="ml-auto text-foreground/65">{SERVICES.length} of 24 shown</span>
                </div>
                <table className="w-full text-[12px]">
                  <thead className="text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                    <tr className="border-b hairline">
                      <th className="text-left px-3 py-1.5">Service</th>
                      <th className="text-left px-3 py-1.5 hidden sm:table-cell">Status</th>
                      <th className="text-right px-3 py-1.5 hidden md:table-cell">rps</th>
                      <th className="text-right px-3 py-1.5">p95</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SERVICES.map((s, i) => (
                      <tr
                        key={s.name}
                        className={
                          "border-b hairline last:border-b-0 transition-opacity hover:bg-foreground/[0.02] " +
                          (tableArmed ? "opacity-100" : "opacity-0")
                        }
                        style={{
                          transitionDelay: tableArmed ? `${i * 60}ms` : undefined,
                          transitionDuration: "400ms",
                        }}
                      >
                        <td className="px-3 py-2 font-mono text-foreground/85">{s.name}</td>
                        <td className="px-3 py-2 hidden sm:table-cell">
                          <StatusPill status={s.status} />
                        </td>
                        <td className="px-3 py-2 text-right hidden md:table-cell font-mono tabular-num text-foreground/65">
                          {s.rps.toLocaleString()}
                        </td>
                        <td
                          className={
                            "px-3 py-2 text-right font-mono tabular-num " +
                            (s.status === "warn"
                              ? "text-[hsl(var(--accent))]"
                              : "text-foreground/75")
                          }
                        >
                          {s.p95} ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Restricted footer */}
            <div className="border-t hairline px-4 py-2.5 bg-foreground/[0.02] flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono tracking-[0.16em] uppercase text-foreground/65">
              <ShieldCheck className="h-3 w-3" />
              <span>Admin authentication required</span>
              <span className="text-foreground/30">·</span>
              <span>activity audited</span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-foreground/65">
                <AlertTriangle className="h-3 w-3" />
                Every click logged
              </span>
            </div>
          </main>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Reference at <span className="font-mono text-foreground/65">dash.askfinz.ai</span>
          <span className="text-foreground/65"> · admin-only, no public sign-up</span>
        </span>
      </div>
    </div>
  );
}

function KpiTile({ kpi }: { kpi: Kpi }) {
  const Icon = kpi.icon;
  // KPI values may be float (e.g. egress 4.8 TB) — NumberTicker rounds.
  // For those we render the number directly with formatting.
  const isFloat = kpi.value % 1 !== 0;
  return (
    <div className="rounded-xl border hairline bg-background p-3.5 flex flex-col gap-1.5">
      <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.16em] uppercase text-foreground/65">
        <Icon className="h-3 w-3" />
        <span className="truncate">{kpi.label}</span>
      </div>
      <div className="text-[22px] font-semibold tracking-tight tabular-num">
        {kpi.prefix}
        {isFloat ? kpi.value.toFixed(1) : <NumberTicker value={kpi.value} />}
        {kpi.suffix && <span className="text-foreground/65 text-[14px] ml-0.5">{kpi.suffix}</span>}
      </div>
      <div className="text-[10px] font-mono tracking-[0.14em] uppercase">
        <span
          className={
            kpi.trend === "up"
              ? "text-foreground/75"
              : kpi.trend === "down"
              ? "text-foreground/65"
              : "text-foreground/65"
          }
        >
          {kpi.delta}
        </span>
        <span className="text-foreground/35"> · vs prior period</span>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Service["status"] }) {
  const map: Record<Service["status"], { dot: string; label: string; text: string }> = {
    ok: { dot: "bg-foreground/60", label: "Healthy", text: "text-foreground/70" },
    warn: { dot: "bg-[hsl(var(--accent))]", label: "Elevated p95", text: "text-[hsl(var(--accent))]" },
    down: { dot: "bg-[hsl(var(--accent))] animate-pulse", label: "Down", text: "text-[hsl(var(--accent))]" },
  };
  const m = map[status];
  return (
    <span className={"inline-flex items-center gap-1.5 text-[11px] font-mono " + m.text}>
      <span className={"h-1.5 w-1.5 rounded-full " + m.dot} />
      {m.label}
    </span>
  );
}

export default DashMock;
